import 'unfonts.css';
import './init';

import '@stripe/stripe-js';
import {
  auth,
  onPageRouteChange,
  redirectOnAuth,
  redirectToContext,
} from 'common-js/api/middleware';
import { Download } from 'common-js/components';
import HoloStore from 'common-js/components/HoloStore';
import * as Paths from 'common-js/constants/paths';
import { createRoot } from 'react-dom/client';
import { Redirect, Route, Router, browserHistory } from 'react-router';

import '../scss/index.scss';

import BillingWrapper from 'billing/containers/BillingWrapper';
import Plan from 'billing/containers/Plan';
import ActivityHistoryDetails from 'devices/components/Activity/ActivityHistoryDetails/ActivityHistoryDetails';
import Layout from 'layouts/Layout';
import LayoutAdmin from 'layouts/LayoutAdmin';
import LayoutBilling from 'layouts/LayoutBilling';
import LayoutHome from 'layouts/LayoutHome';
import LayoutRoutes from 'layouts/LayoutRoutes';
import LayoutSettings from 'layouts/LayoutSettings';
import LayoutUsage from 'layouts/LayoutUsage';
import LayoutWelcome from 'layouts/LayoutWelcome';
import FleetHome from 'home/containers/FleetHome';
import * as Account from './account';
import * as Activation from './activation';
import * as Admin from './admin';
import * as Apps from './apps';
import * as Home from './home';
import * as Orgs from './orgs';
import * as Settings from './settings';
import * as SIMs from './sims';
import * as Usage from './usage';
import * as Welcome from './welcome';

const container = document.getElementById('root');
const root = createRoot(container!);

root.render(
  <HoloStore>
    <Router onUpdate={onPageRouteChange} history={browserHistory}>
      <Route component={Layout}>
        <Route onEnter={auth} path={Paths.ORGS_CREATE_STEP1} component={Orgs.CreateOrg} />
        <Route onEnter={auth} path={Paths.ORGS_CREATE_STEP2} component={Orgs.CreateOrg} />

        <Route onEnter={auth} path={Paths.SIM_INVENTORY} component={SIMs.SimInventory} />
        <Route onEnter={auth} path={Paths.SIM_INVENTORY_ACTIVE} component={SIMs.SimInventory} />
        <Route
          onEnter={auth}
          path={Paths.SIM_INVENTORY_PREDEPLOYMENT}
          component={SIMs.SimInventory}
        />
        <Route
          onEnter={auth}
          path={Paths.SIM_INVENTORY_DEACTIVATED}
          component={SIMs.SimInventory}
        />
        <Route onEnter={auth} path={Paths.SIM_DETAILS} component={SIMs.SimDetails} />
        <Route onEnter={auth} path={Paths.SIM_SIMS_AND_DEVICES} component={SIMs.SimDetails} />
        <Route onEnter={auth} path={Paths.SIM_PROFILES} component={SIMs.SimDetails} />
        <Route onEnter={auth} path={Paths.SIM_RECENT_SESSIONS} component={SIMs.SimDetails} />
        <Route onEnter={auth} path={Paths.SIM_USAGE} component={SIMs.SimDetails} />
        <Redirect from={Paths.SIM_LOCATION_HISTORY} to={Paths.SIM_USAGE} />
        <Route onEnter={auth} path={Paths.SIM_COVERAGE} component={SIMs.SimDetails} />
        <Route onEnter={auth} path={Paths.SIM_PRICING} component={SIMs.SimDetails} />
        <Route onEnter={auth} path={Paths.SIM_WEBHOOKS} component={SIMs.SimDetails} />
        <Route onEnter={auth} path={Paths.SIM_ROUTES} component={SIMs.SimDetails} />
        <Route onEnter={auth} path={Paths.SIM_PLANS} component={SIMs.SimDataPlan} />
        <Route onEnter={auth} path={Paths.SIM_TAG_MANAGER} component={SIMs.SimTagManager} />
        <Route onEnter={auth} path={Paths.SIM_ACTIVITY} component={SIMs.SimActivityHistory} />
        <Route
          onEnter={auth}
          path={Paths.SIM_ACTIVITY_HISTORY_DETAILS}
          component={ActivityHistoryDetails}
        />
      </Route>

      <Route component={LayoutAdmin}>
        <Route onEnter={auth} path={Paths.ADMIN_RELEASEFLAGS} component={Admin.ReleaseFlags} />
        <Route onEnter={auth} path={Paths.ADMIN_IMPERSONATE} component={Admin.Impersonate} />
        <Route path={Paths.ADMIN_SPOOF_LANDING} component={Admin.SpoofLanding} />
      </Route>

      <Route component={LayoutUsage}>
        <Route onEnter={auth} path={Paths.USAGE_LIVE} component={SIMs.RecentSessions} />
        <Route onEnter={auth} path={Paths.USAGE_IN_TESTING} component={Usage.UsagePredeployment} />
        <Route
          onEnter={auth}
          path={Paths.USAGE_EXITED_TESTING}
          component={Usage.UsagePredeployment}
        />
        <Route
          onEnter={auth}
          path={Paths.USAGE_TESTING_INVENTORY}
          component={Usage.UsagePredeployment}
        />
        <Route onEnter={auth} path={Paths.USAGE_BY_DAY} component={Usage.UsageOverviewWrapper} />
        <Route onEnter={auth} path={Paths.USAGE_BY_SIM} component={Usage.UsageOverviewWrapper} />
        <Redirect from={Paths.USAGE_BY_SIM_AND_DAY} to={Paths.USAGE_BY_SIM} />
        <Route
          onEnter={auth}
          path={Paths.USAGE_BY_LOCATION}
          component={Usage.UsageOverviewWrapper}
        />
        <Route
          onEnter={auth}
          path={Paths.USAGE_BY_SESSION}
          component={Usage.UsageOverviewWrapper}
        />
      </Route>

      <Route component={LayoutSettings}>
        <Route onEnter={auth} path={Paths.SETTINGS_ORG} component={Settings.SettingsOrg} />
        <Route onEnter={auth} path={Paths.SETTINGS_ACCOUNT} component={Settings.SettingsAccount} />
        <Route onEnter={auth} path={Paths.SETTINGS_API} component={Settings.SettingsApi} />
      </Route>

      <Route component={LayoutBilling}>
        <Route onEnter={auth} path={Paths.BILLING_ACCOUNT} component={BillingWrapper} />
        <Route onEnter={auth} path={Paths.BILLING_HISTORY} component={BillingWrapper} />
        <Route onEnter={auth} path={Paths.BILLING_ORDERS} component={BillingWrapper} />
        <Route onEnter={auth} path={Paths.BILLING_STATEMENTS} component={BillingWrapper} />
        <Route onEnter={auth} path={Paths.BILLING_INVOICES} component={BillingWrapper} />
        <Route onEnter={auth} path={Paths.BILLING_SETTINGS} component={BillingWrapper} />
        <Route onEnter={auth} path={Paths.BILLING_DATA_PLANS} component={BillingWrapper} />
      </Route>
      <Route component={Layout}>
        <Route onEnter={auth} path={Paths.BILLING_DATA_PLAN} component={Plan} />
        <Route onEnter={auth} path={Paths.BILLING_DATA_PLAN_COVERAGE} component={Plan} />
      </Route>

      <Route component={LayoutRoutes}>
        <Route onEnter={auth} path={Paths.APPS_OVERVIEW} component={Apps.Overview} />
        <Route onEnter={auth} path={Paths.APPS_CONFIGURE} component={Apps.Configure} />
        <Route onEnter={auth} path={Paths.APPS_ADD} component={Apps.Add} />
        <Route
          onEnter={redirectToContext}
          path={Paths.OLD_APPS_OVERVIEW}
          component={Apps.Overview}
        />
        <Route
          onEnter={redirectToContext}
          path={Paths.OLD_APPS_CONFIGURE}
          component={Apps.Configure}
        />
        <Route onEnter={redirectToContext} path={Paths.OLD_APPS_ADD} component={Apps.Add} />
      </Route>

      <Route component={LayoutHome}>
        <Route onEnter={auth} path={Paths.HOME_OVERVIEW} component={FleetHome} />
        <Route
          onEnter={auth}
          path={Paths.ACTIONS_ALERTS_DEVICE_TACCHANGE}
          component={Home.TacChange}
        />
        <Route
          onEnter={auth}
          path={Paths.ACTIONS_ALERTS_DEVICE_DATALIMIT}
          component={Home.DataLimit}
        />
        <Route
          onEnter={auth}
          path={Paths.ACTIONS_ALERTS_DEVICE_HIGHUSAGE}
          component={Home.HighUsage}
        />
      </Route>

      <Route
        path={Paths.ACCOUNT_START}
        onEnter={() => {
          window.location.href = 'https://www.hologram.io/start';
        }}
      />

      <Route component={LayoutWelcome}>
        <Route onEnter={auth} path={Paths.WELCOME} component={Welcome.WelcomeWrapper} />
      </Route>

      <Route onEnter={auth} path={Paths.ACTIVATION} component={Activation.LandingPage} />

      <Route onEnter={auth} path={Paths.DOWNLOAD} component={Download} />

      <Redirect from={Paths.ACCOUNT_OLD_LOGIN} to={Paths.ACCOUNT_LOGIN} />
      <Route onEnter={redirectOnAuth} path={Paths.ACCOUNT_LOGIN} component={Account.Login} />
      <Route path={Paths.ACCOUNT_REGISTER} component={Account.Register} />
      <Route path={Paths.ACCOUNT_FORGOT_PASSWORD} component={Account.ForgotPassword} />
      <Route path={Paths.ACCOUNT_RESET_PASSWORD} component={Account.ResetPassword} />
      <Route path={Paths.ACCOUNT_VERIFY_EMAIL} component={Account.VerifyEmail} />

      <Redirect from="/" to={Paths.ACCOUNT_LOGIN} />
      <Route path="/*" component={Account.FourZeroFour} />
    </Router>
  </HoloStore>
);
